import { useForm, useMutation } from '@meteor/data-hooks';
import {
  Avatar,
  Button,
  CenteredSpinner,
  Entity,
  Error,
  Input,
  Or,
  Spacer,
  Text,
  Title
} from '@meteor/ui';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GithubLogo } from '../components/github';
import { GoogleLogo } from '../components/google';
import { AuthLayout } from '../components/layout';
import { authState } from '../state/auth';

export let AuthHomeScene = ({
  sessionOrUserId,
  nextUrl,
  email,
  type,

  setAuthIntent
}: {
  email: string | undefined;
  type: 'login' | 'signup' | 'switch';
  nextUrl: string;
  sessionOrUserId: string | undefined;

  setAuthIntent?: (d: { authIntentId: string; authIntentClientSecret: string }) => void;
}) => {
  let auth = authState.use({});
  let startAuthentication = useMutation(auth.mutators.start);

  let submitting = useRef(false);
  let [loadingSource, setLoadingSource] = useState<string>();
  let form = useForm({
    initialValues: {
      email: email ?? ''
    },
    schema: yup =>
      yup.object().shape({
        email: yup
          .string()
          .email('Make sure your email is valid')
          .required('Please enter your email')
      }),
    onSubmit: async values => {
      if (submitting.current) return;
      submitting.current = true;

      setLoadingSource('email');

      let [res] = await startAuthentication.mutate({
        type: 'email',
        email: values.email,
        redirectUrl: nextUrl
      });

      submitting.current = false;

      if (!res) return;

      setAuthIntent?.({
        authIntentId: res.id,
        authIntentClientSecret: res.clientSecret
      });
    }
  });

  let canAutoSubmit = form.values.email && email; // && type == 'login';
  let autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (
      email &&
      canAutoSubmit &&
      !startAuthentication.isLoading &&
      !startAuthentication.isSuccess
    ) {
      if (autoSubmittedRef.current) return;
      autoSubmittedRef.current = true;

      form.setFieldValue('email', email);
      form.submitForm();
    }
  }, [canAutoSubmit, email, startAuthentication.isLoading, startAuthentication.isSuccess]);

  let [sessionOrUserIdToLogInWith, setSessionOrUserIdToLogInWith] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (sessionOrUserId) {
      setSessionOrUserIdToLogInWith(sessionOrUserId);
    }
  }, [sessionOrUserId]);

  let loggingInWithUserRef = useRef(false);
  useEffect(() => {
    if (!sessionOrUserIdToLogInWith || loggingInWithUserRef.current) return;
    loggingInWithUserRef.current = true;

    setLoadingSource(`session_${sessionOrUserIdToLogInWith}`);

    startAuthentication
      .mutate({
        type: 'session',
        userOrSessionId: sessionOrUserIdToLogInWith,
        redirectUrl: nextUrl
      })
      .then(([res]) => {
        if (!res) return;

        setAuthIntent?.({
          authIntentId: res.id,
          authIntentClientSecret: res.clientSecret
        });
      });
  }, [sessionOrUserIdToLogInWith]);

  if (auth.error) {
    return (
      <AuthLayout>
        <Error>{auth.error.data?.message ?? auth.error.message ?? 'An error occurred'}</Error>
      </AuthLayout>
    );
  }

  if (((canAutoSubmit || sessionOrUserId) && !startAuthentication.error) || auth.isLoading)
    return (
      <AuthLayout>
        <CenteredSpinner />
      </AuthLayout>
    );

  return (
    <AuthLayout>
      <Title as="h1" weight="bold" size="4">
        {
          {
            login: 'Log in',
            signup: 'Sign up',
            switch: 'Choose account'
          }[type]
        }
      </Title>

      <Spacer height={10} />

      <Text color="gray600" weight="medium" size="2">
        {
          {
            login: `Welcome back! Enter your email to continue.`,
            signup: `Nice to meet you! Enter your email to get started.`,
            switch: `Choose the account you'd like to continue with.`
          }[type]
        }
      </Text>

      <Spacer height={25} />

      {!!auth.data?.users.length && (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10
            }}
          >
            {auth.data.users.map(({ user, ...session }) => (
              <div
                key={user.id}
                onClick={() => setSessionOrUserIdToLogInWith(session.id)}
                style={{ cursor: 'pointer' }}
              >
                <Entity.Wrapper>
                  <Entity.Content>
                    <Entity.Field
                      prefix={<Avatar entity={user} />}
                      title={
                        <>
                          {user.name}{' '}
                          {session.status == 'logged_out' && (
                            <span style={{ opacity: 0.5 }}>(Logged Out)</span>
                          )}
                        </>
                      }
                      value={user.email}
                    />

                    <Entity.Field title="Actions" right>
                      <Button
                        size="1"
                        onClick={() => setSessionOrUserIdToLogInWith(session.id)}
                        disabled={startAuthentication.isLoading}
                        loading={
                          startAuthentication.isLoading &&
                          loadingSource == `session_${session.id}`
                        }
                      >
                        {session.status == 'logged_out' ? 'Login' : 'Choose'}
                      </Button>
                    </Entity.Field>
                  </Entity.Content>
                </Entity.Wrapper>
              </div>
            ))}
          </div>

          <Spacer height={25} />
        </>
      )}

      <form onSubmit={form.handleSubmit}>
        <Input label="Email" {...form.getFieldProps('email')} />
        <form.RenderError field="email" />

        <Spacer height={10} />

        <Button
          fullWidth
          size="3"
          type="submit"
          loading={startAuthentication.isLoading && loadingSource == 'email'}
          disabled={startAuthentication.isLoading}
        >
          Continue
        </Button>
        <startAuthentication.RenderError />

        {type != 'switch' && (
          <>
            <Spacer height={10} />

            <Text align="center" color="gray600" weight="medium" size="1">
              {type == 'login' ? (
                <Link
                  to={`/signup?nextUrl=${encodeURIComponent(nextUrl)}`}
                  style={{ color: 'inherit' }}
                  aria-disabled={startAuthentication.isLoading}
                >
                  Don't have an account?{' '}
                  <span style={{ textDecoration: 'underline' }}>Create one</span>
                </Link>
              ) : (
                <Link
                  to={`/login?nextUrl=${encodeURIComponent(nextUrl)}`}
                  style={{ color: 'inherit' }}
                  aria-disabled={startAuthentication.isLoading}
                >
                  Already have an account?{' '}
                  <span style={{ textDecoration: 'underline' }}>Log in</span>.
                </Link>
              )}
            </Text>
          </>
        )}
      </form>

      <Spacer height={20} />

      <Or />

      <Spacer height={20} />

      <Button
        onClick={() => {
          setLoadingSource('google');
          startAuthentication.mutate({
            type: 'oauth',
            provider: 'google',
            redirectUrl: nextUrl
          });
        }}
        size="2"
        fullWidth
        variant="outline"
        iconLeft={<GoogleLogo />}
        loading={startAuthentication.isLoading && loadingSource == 'google'}
        disabled={startAuthentication.isLoading}
      >
        Google
      </Button>

      <Spacer height={10} />

      <Button
        onClick={() => {
          setLoadingSource('github');
          startAuthentication.mutate({
            type: 'oauth',
            provider: 'github',
            redirectUrl: nextUrl
          });
        }}
        size="2"
        fullWidth
        variant="outline"
        iconLeft={<GithubLogo theme="light" />}
        loading={startAuthentication.isLoading && loadingSource == 'github'}
        disabled={startAuthentication.isLoading}
      >
        Github
      </Button>
    </AuthLayout>
  );
};
